import { Platform } from "@src/mobile/utils/platform";

import { APP_V3_APP_EVENTS, logEvent } from "../../lib/analytics";
import {
  isEmailRemembered,
  isRememberEmailPromptSuppressed,
  rememberEmail,
  REMEMBER_EMAIL_PROMPT_EVENT,
  setPendingRememberEmailPrompt,
} from "./rememberEmailStore";

/**
 * Requests the post-sign-in "remember your email?" prompt for `email`
 * (DEVOP-5493). Non-blocking — call it from a sign-in success handler and let
 * the flow redirect immediately. The prompt is shown by the root-level
 * `RememberEmailPromptManager` *after* the redirect, so it never blocks the
 * redirect, times out the sign-in, or depends on the auth page staying mounted.
 *
 * - No-op when the email is missing, already remembered, or the user has
 *   permanently opted out on this device ("Don't ask again").
 * - On mobile (native app or mobile web browser) the device is personal, so the
 *   email is remembered silently without prompting.
 * - On desktop web, records the email and fires `REMEMBER_EMAIL_PROMPT_EVENT`
 *   for the manager to pick up.
 */
export function requestRememberEmail(email: string): void {
  const trimmed = email.trim();
  if (!trimmed || isRememberEmailPromptSuppressed() || isEmailRemembered(trimmed)) {
    return;
  }

  if (Platform.isMobile || Platform.isMobileBrowser) {
    rememberEmail(trimmed);
    // No prompt is shown here, so this is not a human choice — keep it out of
    // the prompt-choice events.
    logEvent(APP_V3_APP_EVENTS.REMEMBER_EMAIL_AUTO_REMEMBERED);
    return;
  }

  setPendingRememberEmailPrompt(trimmed);
  logEvent(APP_V3_APP_EVENTS.REMEMBER_EMAIL_PROMPT_SHOWN);
  try {
    globalThis.dispatchEvent(new Event(REMEMBER_EMAIL_PROMPT_EVENT));
  } catch {
    // dispatchEvent may be unavailable in non-DOM contexts; the manager also
    // checks for a pending prompt on mount, so the dialog still appears.
  }
}
